export const ASSIGNMENT_POLICY_TYPE_MICRO_CORRIDOR = 'micro_corridor';
export const MICRO_CORRIDOR_FETCH_BATCH_SIZE = 12;

/** Prefer selected services for supply query; fall back to full city catalog. */
export function resolveServiceDetailIdsForMcListing(
  selectedService: any,
  cityServices: any[],
): string[] {
  const selected = resolveServiceDetailIdsFromSelection(selectedService);
  if (selected.length > 0) {
    return selected;
  }
  return resolveServiceDetailIdsFromCatalog(cityServices);
}

export interface MicroCorridorListOption {
  id: string;
  name: string;
  displayLabel?: string;
  workspace?: string;
  serviceDetails?: Array<{ id?: string; name?: string }>;
  isRegisteredInSupply?: boolean;
  isRegisteredInGeoLayers?: boolean;
}

function modeIdFromServiceRow(row: any): string {
  if (!row || !row.modeInfo) {
    return '';
  }
  const modeId = row.modeInfo._id != null ? row.modeInfo._id : row.modeInfo.id;
  return modeId != null ? String(modeId).trim() : '';
}

/** Mode id for supply workspace MC listing from `/get/services` rows. */
export function resolveModeIdForMcListing(selectedService: any, cityServices: any[]): string | null {
  const sdids = resolveServiceDetailIdsFromSelection(selectedService);
  if (sdids.length === 0 || !cityServices || cityServices.length === 0) {
    return null;
  }
  const modeIds = new Set<string>();
  for (const sdid of sdids) {
    const row = cityServices.find((r) => r && String(r._id) === String(sdid));
    const modeId = modeIdFromServiceRow(row);
    if (modeId) {
      modeIds.add(modeId);
    }
  }
  if (modeIds.size === 0) {
    return null;
  }
  if (modeIds.size === 1) {
    return Array.from(modeIds)[0];
  }
  const firstRow = cityServices.find((r) => r && String(r._id) === String(sdids[0]));
  const firstMode = modeIdFromServiceRow(firstRow);
  return firstMode || Array.from(modeIds)[0];
}

function microCorridorNameFromRaw(mc: any): string {
  if (!mc || mc.name == null) {
    return '';
  }
  const s = String(mc.name).trim();
  const id = mc.id != null ? String(mc.id).trim() : '';
  if (!s || (id && s === id)) {
    return '';
  }
  return s;
}

export function attachMicroCorridorDisplayLabels(
  list: MicroCorridorListOption[],
): MicroCorridorListOption[] {
  return list.map((mc) => ({
    ...mc,
    displayLabel: formatMicroCorridorOptionLabel(mc),
  }));
}

export function parseMicroCorridorsApiResponse(res: any): MicroCorridorListOption[] {
  const raw = (res && Array.isArray(res.microCorridors))
    ? res.microCorridors
    : ((res && res.data && Array.isArray(res.data.microCorridors)) ? res.data.microCorridors : []);
  const mapped = raw
    .filter((mc: any) => mc && mc.id)
    .map((mc: any) => ({
      id: String(mc.id),
      name: microCorridorNameFromRaw(mc),
      workspace: mc.workspace != null ? String(mc.workspace).trim() : undefined,
      serviceDetails: Array.isArray(mc.serviceDetails) ? mc.serviceDetails : [],
      isRegisteredInSupply: !!mc.isRegisteredInSupply,
      isRegisteredInGeoLayers: !!mc.isRegisteredInGeoLayers,
    }));
  return attachMicroCorridorDisplayLabels(mapped);
}

export function resolveServiceDetailIdsFromCatalog(cityServices: any[]): string[] {
  if (!cityServices || cityServices.length === 0) {
    return [];
  }
  const ids: string[] = [];
  for (const row of cityServices) {
    if (!row) {
      continue;
    }
    const id = row._id != null
      ? String(row._id)
      : (row.service && row.service._id != null ? String(row.service._id) : '');
    if (id.length > 0 && !ids.includes(id)) {
      ids.push(id);
    }
  }
  return ids;
}

/** Service detail ObjectIds from basic-info selectedService rows. */
export function resolveServiceDetailIdsFromSelection(selectedService: any): string[] {
  if (!selectedService) {
    return [];
  }
  const rows = Array.isArray(selectedService) ? selectedService : [selectedService];
  const ids: string[] = [];
  for (const row of rows) {
    if (!row) {
      continue;
    }
    const id = row._id != null
      ? String(row._id)
      : (row.service && row.service._id != null ? String(row.service._id) : '');
    if (id.length > 0 && !ids.includes(id)) {
      ids.push(id);
    }
  }
  return ids;
}

/** Global service ids from `POST /api/get/services` rows (dashboard config / slot titles only). */
export function resolveGlobalServiceIdsFromSelection(selectedService: any): string[] {
  if (!selectedService) {
    return [];
  }
  const rows = Array.isArray(selectedService) ? selectedService : [selectedService];
  const ids: string[] = [];
  for (const row of rows) {
    if (!row || !row.service || row.service._id == null) {
      continue;
    }
    const id = String(row.service._id);
    if (id.length > 0 && !ids.includes(id)) {
      ids.push(id);
    }
  }
  return ids;
}

export function chunkServiceDetailIds(ids: string[], batchSize: number): string[][] {
  if (batchSize <= 0 || ids.length === 0) {
    return [];
  }
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += batchSize) {
    chunks.push(ids.slice(i, i + batchSize));
  }
  return chunks;
}

export function mergeMicroCorridorOptions(lists: MicroCorridorListOption[][]): MicroCorridorListOption[] {
  const byId = new Map<string, MicroCorridorListOption>();
  for (const list of lists) {
    for (const mc of list) {
      const existing = byId.get(mc.id);
      if (!existing) {
        byId.set(mc.id, { ...mc });
        continue;
      }
      byId.set(mc.id, mergeOneMicroCorridor(existing, mc));
    }
  }
  const merged = Array.from(byId.values()).sort((a, b) =>
    (a.name || a.id).localeCompare(b.name || b.id),
  );
  return attachMicroCorridorDisplayLabels(merged);
}

function mergeOneMicroCorridor(a: MicroCorridorListOption, b: MicroCorridorListOption): MicroCorridorListOption {
  const detailById = new Map<string, { id?: string; name?: string }>();
  const allDetails = [...(a.serviceDetails || []), ...(b.serviceDetails || [])];
  for (const sd of allDetails) {
    if (sd && sd.id) {
      detailById.set(String(sd.id), sd);
    }
  }
  return {
    id: a.id,
    name: a.name || b.name,
    serviceDetails: Array.from(detailById.values()),
    isRegisteredInSupply: !!(a.isRegisteredInSupply || b.isRegisteredInSupply),
    isRegisteredInGeoLayers: !!(a.isRegisteredInGeoLayers || b.isRegisteredInGeoLayers),
  };
}

export type FetchMicroCorridorBatchFn = (cityId: string, serviceDetailIds: string[]) => Promise<any>;

export async function fetchMicroCorridorsBatched(
  cityId: string,
  serviceDetailIds: string[],
  fetchBatch: FetchMicroCorridorBatchFn,
  batchSize: number = MICRO_CORRIDOR_FETCH_BATCH_SIZE,
): Promise<MicroCorridorListOption[]> {
  const chunks = chunkServiceDetailIds(serviceDetailIds, batchSize);
  const parsedBatches: MicroCorridorListOption[][] = [];
  for (const chunk of chunks) {
    try {
      const res = await fetchBatch(cityId, chunk);
      parsedBatches.push(parseMicroCorridorsApiResponse(res));
    } catch (_err) {
      // Partial results allowed when batching on the client (legacy).
    }
  }
  if (parsedBatches.length === 0) {
    throw new Error('Failed to fetch micro-corridors');
  }
  return mergeMicroCorridorOptions(parsedBatches);
}

export function formatMicroCorridorOptionLabel(mc: MicroCorridorListOption): string {
  const id = mc && mc.id != null ? String(mc.id).trim() : '';
  const name = mc && mc.name ? String(mc.name).trim() : '';
  if (name && id) {
    return `${name} - ${id}`;
  }
  return name || id;
}

export function microCorridorHasServiceDetails(mc: MicroCorridorListOption | undefined): boolean {
  return !!(mc && mc.serviceDetails && mc.serviceDetails.length > 0);
}

export function matchCityServicesToMicroCorridor(
  serviceDetails: Array<{ id?: string; name?: string }> | undefined,
  cityServices: any[],
): any[] {
  if (!serviceDetails || serviceDetails.length === 0 || !cityServices || cityServices.length === 0) {
    return [];
  }
  const sdIds = new Set(
    serviceDetails
      .map((sd) => (sd && sd.id ? String(sd.id) : ''))
      .filter((id) => id.length > 0),
  );
  return cityServices.filter((row) => {
    if (!row) {
      return false;
    }
    const serviceDetailId = row._id != null
      ? String(row._id)
      : (row.service && row.service._id != null ? String(row.service._id) : '');
    return serviceDetailId.length > 0 && sdIds.has(serviceDetailId);
  });
}

export const MC_SIGN_IN_SERVICE_ALLOWLIST_ALL = 'all';

function normalizeAllowlistToken(value: string): string {
  return String(value || '').trim().toLowerCase();
}

/**
 * Config allowlist: `['all']`, service names (e.g. `Link`), or Mongo object ids.
 * Prefer nested `service._id` from `POST /api/get/services` — same across cities.
 * Top-level row `_id` (service detail) and `service.newService` also match for legacy entries.
 */
export function isLogisticsV2IncentiveUiEnabled(
  allowlist: string[] | undefined | null,
  selectedServices: any[] | string | null | undefined,
): boolean {
  const entries = (allowlist || [])
    .map(normalizeAllowlistToken)
    .filter((token) => token.length > 0);
  if (entries.length === 0) {
    return false;
  }
  if (entries.indexOf(MC_SIGN_IN_SERVICE_ALLOWLIST_ALL) !== -1) {
    return true;
  }
  const rows = coerceSelectedServiceRows(selectedServices);
  if (rows.length === 0) {
    return false;
  }
  for (let i = 0; i < rows.length; i++) {
    if (serviceRowMatchesAllowlist(rows[i], entries)) {
      return true;
    }
  }
  return false;
}

/** @deprecated use isLogisticsV2IncentiveUiEnabled */
export const isMcSignInIncentiveUiEnabled = isLogisticsV2IncentiveUiEnabled;

export function resolveLogisticsV2IncentiveServiceAllowlist(env: {
  logisticsV2Incentive?: { enabledServices?: string[] };
  logisticsV2IncentiveEnabledServices?: string[];
}): string[] {
  // Delegates to logistics-v2-incentive.config when imported there; keep local fallback for tests.
  const block = env.logisticsV2Incentive;
  if (block && block.enabledServices && block.enabledServices.length) {
    return block.enabledServices;
  }
  const primary = env.logisticsV2IncentiveEnabledServices;
  return primary && primary.length ? primary : ['all'];
}

const LOGISTICS_V2_KNOWN_INCENTIVE_TYPES = new Set([
  'Daily',
  'Weekly Fixed',
  'Adhoc',
]);

/** Maps config shorthand to payroll-dash / API incentive type labels. */
export function normalizeLogisticsV2IncentiveTypeToken(token: string): string | null {
  const trimmed = String(token || '').trim();
  if (!trimmed) {
    return null;
  }
  if (LOGISTICS_V2_KNOWN_INCENTIVE_TYPES.has(trimmed)) {
    return trimmed;
  }
  const lower = trimmed.toLowerCase();
  if (lower === 'weekly' || lower === 'weekly fixed') {
    return 'Weekly Fixed';
  }
  if (lower === 'daily') {
    return 'Daily';
  }
  if (lower === 'adhoc') {
    return 'Adhoc';
  }
  return null;
}

/** Incentive types selectable when incentive version v2 is chosen. Defaults to Daily only. */
export function resolveLogisticsV2AllowedIncentiveTypes(
  configured?: string[] | null,
): string[] {
  const cfg = configured;
  if (!cfg || !cfg.length) {
    return ['Daily'];
  }
  const seen = new Set<string>();
  const allowed: string[] = [];
  for (let i = 0; i < cfg.length; i++) {
    const normalized = normalizeLogisticsV2IncentiveTypeToken(cfg[i]);
    if (!normalized || seen.has(normalized)) {
      continue;
    }
    seen.add(normalized);
    allowed.push(normalized);
  }
  return allowed.length ? allowed : ['Daily'];
}

export function isLogisticsV2IncentiveTypeAllowed(
  type: string | null | undefined,
  allowed: string[],
): boolean {
  if (!type) {
    return false;
  }
  return allowed.indexOf(type) !== -1;
}

export function formatLogisticsV2AllowedIncentiveTypes(allowed: string[]): string {
  if (!allowed || !allowed.length) {
    return 'Daily only';
  }
  if (allowed.length === 1) {
    return `${allowed[0]} only`;
  }
  const last = allowed[allowed.length - 1];
  const rest = allowed.slice(0, -1);
  return `${rest.join(', ')} and ${last}`;
}

function coerceSelectedServiceRows(selected: any[] | string | null | undefined): any[] {
  if (!selected) {
    return [];
  }
  if (selected instanceof Array) {
    return selected.filter((row) => !!row);
  }
  return [];
}

function looksLikeMongoObjectId(token: string): boolean {
  return /^[a-f0-9]{24}$/i.test(token);
}

function collectServiceObjectIdTokens(row: any): string[] {
  const tokens: string[] = [];
  if (!row) {
    return tokens;
  }
  if (row._id != null) {
    tokens.push(normalizeAllowlistToken(String(row._id)));
  }
  if (row.service) {
    if (row.service._id != null) {
      tokens.push(normalizeAllowlistToken(String(row.service._id)));
    }
    if (row.service.newService != null) {
      tokens.push(normalizeAllowlistToken(String(row.service.newService)));
    }
  }
  return tokens;
}

function serviceRowMatchesAllowlist(row: any, entries: string[]): boolean {
  if (!row) {
    return false;
  }
  const objectIdTokens = collectServiceObjectIdTokens(row);
  const nameTokens = collectServiceNameTokens(row);

  for (let e = 0; e < entries.length; e++) {
    const entry = entries[e];
    if (looksLikeMongoObjectId(entry)) {
      if (objectIdTokens.indexOf(entry) !== -1) {
        return true;
      }
      continue;
    }
    if (nameTokens.indexOf(entry) !== -1) {
      return true;
    }
  }
  return false;
}

function collectServiceNameTokens(row: any): string[] {
  const tokens: string[] = [];
  if (!row || !row.service) {
    return tokens;
  }
  const svc = row.service;
  if (svc.name) {
    tokens.push(normalizeAllowlistToken(String(svc.name)));
  }
  if (svc.shortName) {
    tokens.push(normalizeAllowlistToken(String(svc.shortName)));
  }
  if (svc.displayName) {
    tokens.push(normalizeAllowlistToken(String(svc.displayName)));
  }
  return tokens;
}
